import { captureException, setUser as sentrySetUser } from "@sentry/react";
import type { User } from "firebase/auth";
import { create } from "zustand";
import type { FirebaseSession } from "@/lib/firebase";
import { SENTRY_ENABLED, sentryLogger } from "@/lib/sentry";
import {
	isSocketOwner,
	noteSocketUrlIdentity,
	registerSurface,
	releaseForeignSocket,
	socketBelongsToAnotherUser,
} from "@/lib/socket-owner";
import { requestAuthToken } from "../api";
import { ws } from "../websocket-wrapper";

const WS_BASE_URL = import.meta.env.VITE_WS_URL || "ws://localhost:3000/ws";

export type AuthState = {
	/** Which independent session this store drives, `app` or `operations`. */
	surface: FirebaseSession["surface"];
	user: User | null;
	loading: boolean;
	initialized: boolean;
	wsConnected: boolean;
	login: (email: string, password: string) => Promise<void>;
	signup: (email: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
	connectWebSocket: () => Promise<void>;
};

/**
 * One auth store per independent Firebase session. Two exist: the session behind
 * `/app` and the operations session behind `/operations` (see `lib/firebase.ts`
 * for why they are separate Firebase apps).
 *
 * Neither store owns the socket outright. `lib/socket-owner.ts` arbitrates that,
 * and every connect here is gated on owning it. Without the gate, both sessions
 * would race onto the single `ws` whenever both had a restored user, and the
 * loser's `UPDATE_STATE` frames would land in the shared `core-store`.
 */
export const createAuthStore = (session: FirebaseSession) => {
	const { surface } = session;

	return create<AuthState>((set, get) => {
		/**
		 * Bumped on EVERY auth transition, so a token exchange still in flight can
		 * tell that it no longer speaks for this session (TECH-3247).
		 *
		 * Two exchanges genuinely overlap on a cold LTI launch. Firebase restores the
		 * previous user and starts one, the launch signs the new user in and starts
		 * another, and neither socket exists yet, so `socketBelongsToAnotherUser` has
		 * nothing to look at and cannot help. Whichever exchange resolves LAST would
		 * otherwise win `urlUid` and `wsUrl`, and if that is the restored user's, the
		 * tab dials as THEM while `ws.connect()` no-ops for the launched user. That is
		 * the original bug, with the identity record now confirming the wrong answer.
		 */
		let authGeneration = 0;

		/**
		 * The `?token=` exchange: Firebase ID token → one-time server ULID → URL.
		 *
		 * Answers whether the result was APPLIED. False means a newer auth transition
		 * superseded this exchange mid-flight, and the minted token is dropped
		 * unspent. The newer callback is already running its own exchange, and the
		 * caller must not connect on the strength of this one.
		 */
		const refreshSocketUrl = async (): Promise<boolean> => {
			const generation = authGeneration;
			// Token AND uid come from one snapshot of `currentUser`. Reading the uid back
			// afterwards would let a sign-in landing mid-exchange (the LTI launch) record
			// the NEW user against a URL carrying the previous user's credential, which is
			// the one thing the identity axis below must never believe.
			const credential = await session.getIdTokenWithUid();
			if (!credential) {
				throw new Error("No Firebase ID token available");
			}
			const authToken = await requestAuthToken(credential.idToken);
			if (authGeneration !== generation) {
				sentryLogger.info("Discarded a superseded socket token exchange", {
					surface,
				});
				return false;
			}
			console.log(`[WebSocket Client] Got auth token for ${surface}`);
			noteSocketUrlIdentity(credential.uid);
			ws.updateUrl(`${WS_BASE_URL}?token=${authToken}`, false);
			return true;
		};

		const syncSentryUser = (): void => {
			if (!SENTRY_ENABLED) {
				return;
			}
			const user = get().user;
			sentrySetUser(
				user ? { id: user.uid, email: user.email ?? undefined } : null
			);
		};

		registerSurface(surface, {
			hasUser: () => get().user !== null,
			connect: () => get().connectWebSocket(),
			// A superseded exchange must THROW here, unlike on the connect path. The
			// wrapper reconnects with whatever `wsUrl` holds the moment this resolves,
			// and that would be a token already spent (or another user's). Throwing
			// sends `scheduleReconnect` round again, and the retry mints for whoever is
			// signed in by then.
			refreshUrl: async () => {
				if (!(await refreshSocketUrl())) {
					throw new Error("Auth changed during the token exchange");
				}
			},
			setWsConnected: (wsConnected) => set({ wsConnected }),
			syncSentryUser,
		});

		/**
		 * Clear what this session owns on sign-out. Reached by BOTH explicit logout
		 * and token expiry/revocation, so it is the reliable catch-all for keeping
		 * one account's content off a shared device.
		 *
		 * Scoping matters now that several sessions coexist. `ui-store` (drafts,
		 * gridItems, workspace selection) belongs to the `/app` session and
		 * `ops-ui-store` to the operations session, so each clears only its own.
		 * `core-store` and `data-cache-store` hold whichever session currently owns
		 * the socket, so they are cleared only by that owner. Otherwise a background
		 * token revocation on the *idle* session would wipe the live one's loaded
		 * workspace out from under it.
		 *
		 * Exhaustive rather than an if/else on purpose (TECH-3200). This was a
		 * two-branch `if (surface === "app") … else`, which meant any third surface
		 * fell into the `else` and cleared the OPERATIONS session's state on every
		 * sign-out, wiping a live admin's workspace selection and open tabs when
		 * someone signed out of an unrelated surface, with no type error to catch it.
		 * The `never` below is what makes a fourth surface a compile failure instead
		 * of a silent one.
		 */
		const resetOwnedState = async (): Promise<void> => {
			switch (surface) {
				case "app": {
					const { uiStore } = await import("./ui-store");
					uiStore.getState().reset();
					break;
				}
				case "operations": {
					const { opsUiStore } = await import("./ops-ui-store");
					opsUiStore.getState().reset();
					break;
				}
				case "organization":
					// Nothing persisted of its own. The surface holds no workspace
					// selection and no tab state, and its one list is refetched on entry.
					// A store existing only to be reset here would be worse than this
					// sentence.
					break;
				default: {
					const unreachable: never = surface;
					throw new Error(`Unhandled auth surface: ${String(unreachable)}`);
				}
			}

			if (!isSocketOwner(surface)) {
				return;
			}
			const [{ coreStore }, { dataCacheStore }] = await Promise.all([
				import("./core-store"),
				import("./data-cache-store"),
			]);
			coreStore.getState().reset();
			dataCacheStore.getState().reset();
		};

		const handleSignedIn = async (user: User): Promise<void> => {
			sentryLogger.info("User authenticated", { userId: user.uid, surface });
			// Only the socket-owning surface connects. The idle session just holds its
			// restored user until its route is entered and claims the socket.
			if (!isSocketOwner(surface)) {
				return;
			}
			// A sign-in on top of ANOTHER user's live socket (the LTI launch landing on
			// a restored session, see `lib/lti/launch-flow.ts`) must close that socket
			// before connecting. `connectWebSocket` alone cannot. It mints the right
			// token into `wsUrl`, then `ws.connect()` sees an open socket and returns
			// early, leaving the tab on the previous user's connection with every
			// procedure, `workspace.list` included, resolving against them.
			if (socketBelongsToAnotherUser(user.uid)) {
				sentryLogger.info(
					"Socket held by another user; releasing before connect",
					{
						surface,
						userId: user.uid,
					}
				);
				await releaseForeignSocket();
			}
			syncSentryUser();
			try {
				await get().connectWebSocket();
			} catch (error) {
				console.error("[WebSocket Client] Failed to connect:", error);
				sentryLogger.error("WebSocket connection failed after auth", {
					userId: user.uid,
					surface,
				});
				if (SENTRY_ENABLED) {
					captureException(error, { tags: { context: "websocket_connect" } });
				}
			}
		};

		const handleSignedOut = async (): Promise<void> => {
			sentryLogger.info("User signed out", { surface });
			if (isSocketOwner(surface)) {
				if (SENTRY_ENABLED) {
					sentrySetUser(null);
				}
				ws.disconnect();
				ws.clearMessageQueue();
				ws.clearPendingCalls();
			}
			await resetOwnedState();
		};

		session.subscribeToAuthChanges(async (user) => {
			// Before anything awaits. Every transition supersedes an exchange in flight:
			// a different user, a sign-out, or a token refresh re-emitting the same one.
			authGeneration += 1;
			set({ user, loading: false, initialized: true });
			await (user ? handleSignedIn(user) : handleSignedOut());
		});

		return {
			surface,
			user: null,
			loading: true,
			initialized: false,
			wsConnected: false,

			login: async (email: string, password: string) => {
				set({ loading: true });
				try {
					await session.login(email, password);
					sentryLogger.info("User login successful", { email, surface });
				} catch (error) {
					sentryLogger.error("User login failed", { email, surface });
					set({ loading: false });
					throw error;
				}
			},

			signup: async (email: string, password: string) => {
				set({ loading: true });
				try {
					await session.signup(email, password);
					sentryLogger.info("User signup successful", { email, surface });
				} catch (error) {
					sentryLogger.error("User signup failed", { email, surface });
					set({ loading: false });
					throw error;
				}
			},

			logout: async () => {
				set({ loading: true });
				try {
					await session.logout();
					sentryLogger.info("User logout successful", { surface });
				} catch (error) {
					sentryLogger.error("User logout failed", { surface });
					set({ loading: false });
					throw error;
				}
			},

			connectWebSocket: async () => {
				console.log(
					`[WebSocket Client] Requesting new auth token (${surface})...`
				);
				if (!(await refreshSocketUrl())) {
					// Superseded mid-exchange. Connecting now would dial with a URL the
					// newer sign-in is about to replace, or worse, win the race and hand
					// the newer user the older one's connection.
					return;
				}
				console.log("[WebSocket Client] Connecting...");
				ws.connect();
			},
		};
	});
};
